/**
 * Module-level capture of the `beforeinstallprompt` event.
 *
 * Chrome fires this event once the PWA install criteria are met (manifest
 * + SW + engagement heuristic). It must be `preventDefault()`-ed before
 * the browser shows its own mini-infobar; we keep the event so any UI in
 * the app can later call `.prompt()` to show the real install dialog.
 *
 * This module is loaded as a side effect from `main.tsx` so the listener
 * registers as early as possible — before any React component mounts. If a
 * downstream component listens for changes, it gets the current value via
 * `subscribe()`.
 *
 * iOS Safari never fires the event — there is no programmatic install on
 * iOS. UI must fall back to visual Share→Add instructions there.
 */

export interface BeforeInstallPromptEvent extends Event {
  readonly platforms: readonly string[];
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

type Listener = (event: BeforeInstallPromptEvent | null) => void;

let captured: BeforeInstallPromptEvent | null = null;
const listeners = new Set<Listener>();

function notify() {
  listeners.forEach((l) => l(captured));
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    captured = e as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    captured = null;
    notify();
  });
}

export function getDeferredPrompt(): BeforeInstallPromptEvent | null {
  return captured;
}

export function subscribeToInstallPrompt(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export async function triggerNativeInstall(): Promise<
  'accepted' | 'dismissed' | 'unavailable'
> {
  if (!captured) return 'unavailable';
  const e = captured;
  try {
    await e.prompt();
    const choice = await e.userChoice;
    captured = null;
    notify();
    return choice.outcome;
  } catch {
    captured = null;
    notify();
    return 'dismissed';
  }
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  if ((window.navigator as { standalone?: boolean }).standalone === true) return true;
  return false;
}

export type Platform = 'ios' | 'android' | 'desktop' | 'installed';

export function detectPlatform(): Platform {
  if (typeof window === 'undefined') return 'desktop';
  if (isStandalone()) return 'installed';
  const ua = window.navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua) && !(window as { MSStream?: unknown }).MSStream) return 'ios';
  // iPadOS 13+ says it is a Mac. A Mac with a touch screen is an iPad.
  if (/Macintosh/.test(ua) && window.navigator.maxTouchPoints > 1) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'desktop';
}

/**
 * Social apps open links in their own built-in browser, and those browsers
 * cannot add anything to the home screen — no Share → "Add to Home Screen" on
 * iPhone, no install event on Android. Much of fluent.ge's traffic will arrive
 * exactly this way (TikTok, Instagram, Facebook posts), so showing install
 * steps there would be showing steps that cannot work.
 */
export function isInAppBrowser(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  return /FBAN|FBAV|FB_IAB|Instagram|musical_ly|BytedanceWebview|TikTok|Line\/|Snapchat|Twitter/i.test(ua);
}
